import { Routes, Route, Link } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './lib/auth';
import { Masthead } from './components/Masthead';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { SelectorPage } from './pages/SelectorPage';
import { PreviewPage } from './pages/PreviewPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { CreateWorkspacePage } from './pages/CreateWorkspacePage';
import { WorkspaceDetailPage } from './pages/WorkspaceDetailPage';
import { CreateChecklistPage } from './pages/CreateChecklistPage';
import { ChecklistEditPage } from './pages/ChecklistEditPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AdminApprovalsPage } from './pages/AdminApprovalsPage';

/* The Psych Safety Assessment ships ~70 KB of data + narrative engine that
   nobody who only uses CYBHI needs to load. Lazy-loading these routes drops
   the first-paint bundle for everyone else. */
const PsychHomePage    = lazy(() => import('./pages/psych/PsychHomePage')   .then((m) => ({ default: m.PsychHomePage })));
const PsychTakePage    = lazy(() => import('./pages/psych/PsychTakePage')   .then((m) => ({ default: m.PsychTakePage })));
const PsychResultsPage = lazy(() => import('./pages/psych/PsychResultsPage').then((m) => ({ default: m.PsychResultsPage })));
const PsychHistoryPage = lazy(() => import('./pages/psych/PsychHistoryPage').then((m) => ({ default: m.PsychHistoryPage })));

function PageFallback() {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      Loading…
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen flex flex-col bg-paper">
        <Masthead />
        <main className="flex-1">
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/select" element={<SelectorPage />} />
              <Route path="/preview" element={<PreviewPage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/workspace/new" element={<CreateWorkspacePage />} />
              <Route path="/workspace/:id" element={<WorkspaceDetailPage />} />
              <Route path="/workspace/:id/checklist/new" element={<CreateChecklistPage />} />
              <Route path="/workspace/:id/checklist/:cid" element={<ChecklistEditPage />} />
              <Route path="/invite/:token" element={<AcceptInvitePage />} />
              <Route path="/admin/approvals" element={<AdminApprovalsPage />} />
              <Route path="/assessment" element={<PsychHomePage />} />
              <Route path="/assessment/take" element={<PsychTakePage />} />
              <Route path="/assessment/results" element={<PsychResultsPage />} />
              <Route path="/assessment/results/:id" element={<PsychResultsPage />} />
              <Route path="/assessment/history" element={<PsychHistoryPage />} />
              <Route
                path="*"
                element={
                  <div className="max-w-3xl mx-auto px-6 py-24">
                    <div className="eyebrow mb-4">Not found</div>
                    <h1 className="editorial-h1 mb-4">
                      This page <em className="italic text-accent">isn't here.</em>
                    </h1>
                    <p className="editorial-deck mb-8">
                      We're still building. Come back soon, or head home.
                    </p>
                    <Link to="/" className="btn-text">
                      Go home
                    </Link>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </AuthProvider>
  );
}
